import React from "react";
import styled, { css } from "styled-components";
import { Eye, File, Folder, Minus, Plus } from "react-feather";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { rawColors } from "../../../design-system/tokens/colors";
import { Checkbox } from "../Checkbox";

export type TreeNodeKind = "schema" | "table";

export interface TreeNodeRowProps {
  kind: TreeNodeKind;
  name: string;

  /** Schema rows only. */
  expanded?: boolean;
  onToggleExpand?: () => void;

  /** Table rows only — checkbox state. */
  selected?: boolean;
  onToggleSelect?: (next: boolean) => void;

  /** Table rows only — visually marks the row as the current preview target. */
  previewActive?: boolean;
  onPreviewClick?: () => void;

  /**
   * Table rows only — whether this row can be previewed AND selected.
   * Defaults to `true` so every existing caller (Objects, Snowflake tables,
   * etc.) keeps today's behavior unchanged. Set to `false` for row kinds
   * that are neither previewable nor selectable (e.g. Salesforce
   * reports/dashboards in the native connector browse tree, which sync
   * unconditionally rather than by user selection) — hides the eye icon,
   * makes the row non-clickable for preview, and disables the checkbox.
   */
  previewable?: boolean;
}

// Table rows are indented so the guide line drawn by the molecule passes
// to the left of the checkbox column.
const TABLE_INDENT = "32px";

const Row = styled.div<{
  $kind: TreeNodeKind;
  $isTableActive?: boolean;
}>`
  position: relative;
  display: grid;
  width: 100%;
  align-items: center;
  grid-template-columns: ${({ $kind }) =>
    $kind === "schema" ? "12px 14px 1fr" : "16px 14px 45% 22px"};
  column-gap: ${spacing[2]};
  padding: ${({ $kind }) =>
    $kind === "schema"
      ? `${spacing[2]} ${spacing[3]}`
      : `${spacing[1]} ${spacing[3]} ${spacing[1]} ${TABLE_INDENT}`};
  cursor: pointer;
  ${typographyMixin("body", 4)}
  color: ${rawColors.text.highEmphasis};
  ${({ $kind, $isTableActive }) =>
    $kind === "table" &&
    $isTableActive &&
    css`
      background-color: ${rawColors.primary[50]};
    `}
  &:hover {
    background-color: ${({ $kind, $isTableActive }) =>
      $kind === "table" && $isTableActive
        ? rawColors.primary[50]
        : rawColors.stroke[100]};
  }
`;

const ExpandButton = styled.button`
  width: 12px;
  height: 12px;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: transparent;
  border: none;
  color: ${rawColors.text.mediumEmphasis};
  cursor: pointer;
`;

const Icon = styled.span<{ $isHighlighted?: boolean }>`
  width: 14px;
  height: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ $isHighlighted }) =>
    $isHighlighted ? rawColors.primary[500] : rawColors.text.mediumEmphasis};
`;

const Name = styled.span<{ $isHighlighted: boolean }>`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ $isHighlighted }) =>
    $isHighlighted &&
    css`
      ${typographyMixin("body", 3)}
      color: ${rawColors.primary[500]};
    `}
`;

const PreviewButton = styled.button<{ $active: boolean }>`
  width: 18px;
  height: 18px;
  min-width: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  justify-self: end;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ $active }) =>
    $active ? rawColors.primary[500] : rawColors.text.mediumEmphasis};

  ${({ $active }) =>
    $active &&
    css`
      color: ${rawColors.primary[500]};
    `}

  &:hover {
    color: ${rawColors.primary[500]};
  }
`;

const stop = (e: React.SyntheticEvent) => e.stopPropagation();

export const TreeNodeRow: React.FC<TreeNodeRowProps> = ({
  kind,
  name,
  expanded = false,
  onToggleExpand,
  selected = false,
  onToggleSelect,
  previewActive = false,
  onPreviewClick,
  previewable = true,
}) => {
  if (kind === "schema") {
    return (
      <Row
        $kind="schema"
        role="treeitem"
        aria-expanded={expanded}
        onClick={onToggleExpand}
      >
        <ExpandButton
          type="button"
          aria-label={expanded ? `Collapse ${name}` : `Expand ${name}`}
          onClick={(e) => {
            stop(e);
            onToggleExpand?.();
          }}
        >
          {expanded ? (
            <Minus size={12} strokeWidth={2} />
          ) : (
            <Plus size={12} strokeWidth={2} />
          )}
        </ExpandButton>
        <Icon aria-hidden>
          <Folder size={14} />
        </Icon>
        <Name $isHighlighted={false}>{name}</Name>
      </Row>
    );
  }

  return (
    <Row
      $kind="table"
      $isTableActive={selected || previewActive}
      role="treeitem"
      aria-selected={selected}
      onClick={previewable ? onPreviewClick : undefined}
    >
      <span onClick={stop}>
        <Checkbox
          checked={selected}
          disabled={!previewable}
          onChange={(next) => onToggleSelect?.(next)}
          ariaLabel={`Select ${name}`}
        />
      </span>
      <Icon $isHighlighted={selected || previewActive} aria-hidden>
        <File size={14} />
      </Icon>
      <Name $isHighlighted={selected || previewActive}>{name}</Name>
      {previewable && (
        <PreviewButton
          $active={previewActive}
          type="button"
          aria-label={`Preview ${name}`}
          onClick={(e) => {
            stop(e);
            onPreviewClick?.();
          }}
        >
          <Eye size={13} />
        </PreviewButton>
      )}
    </Row>
  );
};

export default TreeNodeRow;
